import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Skeleton,
    TextField,
    Grid,
    Typography
} from '@mui/material';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import useAuthorize from '../hooks/useAuthorize';
import { MARKETING_DASHBOARD_ROLES } from '../roles/portalRoles';
import CustomerTrendChart from './CustomerTrendChart';
import CustomerConversionChart from './CustomerConversionChart';
import VisitorTrendChart from './VisitorTrendChart';
import VisitorBreakdownCharts from './VisitorBreakdownCharts';
import VisitorLoginSplitChart from './VisitorLoginSplitChart';
import {
    ActiveDormantCustomersChart,
    NewReturningVisitorConversionChart,
    TimeToRegisterProfileChart,
} from './CustomerInsightCharts';
import axios from 'axios';
import { AnalyticsFreshnessNote } from '../utils/AnalyticsFreshnessNote';

const DashboardCustomers = () => {
    useAuthorize(MARKETING_DASHBOARD_ROLES);

    // Default date range: first day of the current year through today.
    const getDefaultDateRange = () => {
        const now = new Date();
        const startDate = startOfYear(now);

        return {
            fromDate: format(startDate, 'yyyy-MM-dd'),
            toDate: format(now, 'yyyy-MM-dd')
        };
    };

    const defaultDates = getDefaultDateRange();

    const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly'>('monthly');
    const [fromDate, setFromDate] = useState<string>(defaultDates.fromDate);
    const [toDate, setToDate] = useState<string>(defaultDates.toDate);
    const [quickRange, setQuickRange] = useState<string>('');
    const [trendData, setTrendData] = useState<any[]>([]);
    const [conversionData, setConversionData] = useState<any[]>([]);
    const [visitorData, setVisitorData] = useState<any[]>([]);
    const [visitorLoginSplit, setVisitorLoginSplit] = useState<any[]>([]);
    const [customerInsights, setCustomerInsights] = useState<any | null>(null);
    const [visitorBreakdowns, setVisitorBreakdowns] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [conversionLoading, setConversionLoading] = useState(true);
    const [visitorLoading, setVisitorLoading] = useState(true);
    const [visitorLoginSplitLoading, setVisitorLoginSplitLoading] = useState(true);
    const [customerInsightsLoading, setCustomerInsightsLoading] = useState(true);
    const [visitorBreakdownLoading, setVisitorBreakdownLoading] = useState(true);
    const [dataThrough, setDataThrough] = useState<string | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

    const captureDataThrough = (response: any) => {
        const nextDataThrough =
            response.headers?.['x-analytics-data-through'] ||
            response.data?.data_through ||
            (Array.isArray(response.data) ? response.data[0]?.data_through : null);

        const nextLastUpdated =
            response.headers?.['x-analytics-last-updated'] ||
            response.data?.last_updated_at ||
            (Array.isArray(response.data) ? response.data[0]?.last_updated_at : null);

        if (nextDataThrough) {
            setDataThrough(nextDataThrough);
        }
        if (nextLastUpdated) {
            setLastUpdatedAt(nextLastUpdated);
        }
    };

    useEffect(() => {
        const fetchTrendData = async () => {
            setLoading(true);
            const apiUrl = `${process.env.REACT_APP_API_URL}/customers/trends`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching customer trends with params:', {
                apiUrl,
                params,
                fullUrl: `${apiUrl}?period=${period}&from_date=${fromDate}&to_date=${toDate}`
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ API Response Status:', response.status);
                console.log('✅ Customer trends data:', response.data);
                console.log('✅ Data length:', response.data?.length || 0);
                captureDataThrough(response);

                if (response.data && Array.isArray(response.data)) {
                    setTrendData(response.data);
                } else {
                    console.warn('⚠️ Response data is not an array:', response.data);
                    setTrendData([]);
                }
            } catch (error: any) {
                console.error('❌ Error fetching customer trends:', error);
                console.error('❌ Error details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                    statusText: error.response?.statusText
                });
                setTrendData([]);
            } finally {
                setLoading(false);
            }
        };

        const fetchConversionData = async () => {
            setConversionLoading(true);
            const apiUrl = `${process.env.REACT_APP_API_URL}/customers/conversion-trends`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching conversion trends with params:', {
                apiUrl,
                params
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ Conversion API Response Status:', response.status);
                console.log('✅ Conversion trends data:', response.data);
                captureDataThrough(response);

                if (response.data && Array.isArray(response.data)) {
                    setConversionData(response.data);
                } else {
                    console.warn('⚠️ Conversion response data is not an array:', response.data);
                    setConversionData([]);
                }
            } catch (error: any) {
                console.error('❌ Error fetching conversion trends:', error);
                setConversionData([]);
            } finally {
                setConversionLoading(false);
            }
        };

        const fetchVisitorData = async () => {
            setVisitorLoading(true);
            const apiUrl = `${process.env.REACT_APP_API_URL}/customers/visitor-trends`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching visitor trends with params:', {
                apiUrl,
                params
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ Visitor Trends API Response Status:', response.status);
                console.log('✅ Visitor trends data:', response.data);
                captureDataThrough(response);

                if (response.data && Array.isArray(response.data)) {
                    setVisitorData(response.data);
                } else {
                    console.warn('⚠️ Visitor response data is not an array:', response.data);
                    setVisitorData([]);
                }
            } catch (error: any) {
                console.error('❌ Error fetching visitor trends:', error);
                setVisitorData([]);
            } finally {
                setVisitorLoading(false);
            }
        };

        const fetchVisitorBreakdowns = async () => {
            setVisitorBreakdownLoading(true);
            const apiUrl = `${process.env.REACT_APP_API_URL}/customers/visitor-breakdowns`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching visitor breakdowns with params:', {
                apiUrl,
                params
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ Visitor breakdowns API Response Status:', response.status);
                captureDataThrough(response);
                setVisitorBreakdowns(response.data || null);
            } catch (error: any) {
                console.error('❌ Error fetching visitor breakdowns:', error);
                setVisitorBreakdowns(null);
            } finally {
                setVisitorBreakdownLoading(false);
            }
        };

        const fetchVisitorLoginSplit = async () => {
            setVisitorLoginSplitLoading(true);
            const apiUrl = `${process.env.REACT_APP_API_URL}/customers/visitor-login-split`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching visitor login split with params:', {
                apiUrl,
                params
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ Visitor Login Split API Response Status:', response.status);
                captureDataThrough(response);
                if (response.data && Array.isArray(response.data)) {
                    setVisitorLoginSplit(response.data);
                } else {
                    setVisitorLoginSplit([]);
                }
            } catch (error: any) {
                console.error('❌ Error fetching visitor login split:', error);
                setVisitorLoginSplit([]);
            } finally {
                setVisitorLoginSplitLoading(false);
            }
        };

        const fetchCustomerInsights = async () => {
            setCustomerInsightsLoading(true);
            const apiUrl = `${process.env.REACT_APP_API_URL}/customers/customer-insights`;
            const params = {
                period: period,
                from_date: fromDate,
                to_date: toDate
            };

            console.log('🔍 Fetching customer insights with params:', {
                apiUrl,
                params
            });

            try {
                const response = await axios.get(apiUrl, { params });
                console.log('✅ Customer Insights API Response Status:', response.status);
                captureDataThrough(response);
                setCustomerInsights(response.data || null);
            } catch (error: any) {
                console.error('❌ Error fetching customer insights:', error);
                setCustomerInsights(null);
            } finally {
                setCustomerInsightsLoading(false);
            }
        };

        fetchTrendData();
        fetchConversionData();
        fetchVisitorData();
        fetchVisitorLoginSplit();
        fetchCustomerInsights();
        fetchVisitorBreakdowns();
    }, [period, fromDate, toDate]);

    const handlePeriodChange = (event: any) => {
        setPeriod(event.target.value);
    };

    const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFromDate(event.target.value);
        setQuickRange(''); // Reset quick range when manually changing dates
    };

    const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setToDate(event.target.value);
        setQuickRange(''); // Reset quick range when manually changing dates
    };

    const handleQuickRangeChange = (event: any) => {
        const range = event.target.value;
        setQuickRange(range);

        if (!range) {
            return; // Don't update dates if "Custom" is selected
        }

        const now = new Date();
        let from = now;
        let to = now;

        switch (range) {
            case 'today':
                from = to = now;
                break;
            case 'yesterday':
                from = to = subDays(now, 1);
                break;
            case 'last7':
                from = subDays(now, 6);
                to = now;
                break;
            case 'thisWeek':
                from = startOfWeek(now, { weekStartsOn: 1 }); // Monday start
                to = endOfWeek(now, { weekStartsOn: 1 });
                break;
            case 'thisMonth':
                from = startOfMonth(now);
                to = endOfMonth(now);
                break;
            default:
                break;
        }

        setFromDate(format(from, 'yyyy-MM-dd'));
        setToDate(format(to, 'yyyy-MM-dd'));
    };

    return (
        <Box sx={{ padding: 2 }}>
            <Card>
                <CardHeader
                    title="Customer Onboarding Trends"
                    subheader="Shows how many people became customers by registering with their mobile number during the selected date range."
                />
                <CardContent>
                    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Period</InputLabel>
                                <Select
                                    value={period}
                                    onChange={handlePeriodChange}
                                    label="Period"
                                >
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Quick Range</InputLabel>
                                <Select value={quickRange} onChange={handleQuickRangeChange} label="Quick Range">
                                    <MenuItem value="">Custom</MenuItem>
                                    <MenuItem value="today">Today</MenuItem>
                                    <MenuItem value="yesterday">Yesterday</MenuItem>
                                    <MenuItem value="last7">Last 7 Days</MenuItem>
                                    <MenuItem value="thisWeek">This Week</MenuItem>
                                    <MenuItem value="thisMonth">This Month</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    {dataThrough && (
                        <AnalyticsFreshnessNote dataThrough={dataThrough} lastUpdatedAt={lastUpdatedAt} />
                    )}
                    {loading ? (
                        <Skeleton height={300} />
                    ) : trendData.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                            <p>No customer data available for the selected date range</p>
                            <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', textAlign: 'center' }}>
                                <p><strong>Debug Info:</strong></p>
                                <p>Period: {period}</p>
                                <p>From Date: {fromDate}</p>
                                <p>To Date: {toDate}</p>
                                <p>API URL: {process.env.REACT_APP_API_URL}/customers/trends</p>
                                <p style={{ marginTop: '10px', fontSize: '0.75rem' }}>
                                    Check browser console (F12) and server logs for detailed debugging information
                                </p>
                            </Box>
                        </Box>
                    ) : (
                        <CustomerTrendChart data={trendData} period={period} />
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: 3 }}>
                <CardHeader
                    title="Customer Profile Completion"
                    subheader="Shows registered customers who still have a temporary user ID versus customers who have updated their name, during the selected date range."
                />
                <CardContent>
                    {conversionLoading ? (
                        <Skeleton height={360} />
                    ) : conversionData.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No profile completion data available for the selected date range</p>
                        </Box>
                    ) : (
                        <CustomerConversionChart data={conversionData} period={period} />
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: 3 }}>
                <CardHeader
                    title="New vs Returning Visitor Conversion"
                    subheader="Shows which visitors are more likely to become customers. A new visitor is in their first visit period. A returning visitor has visited before. Registration rate means the percentage of those visitors who registered in that same period."
                />
                <CardContent>
                    {customerInsightsLoading ? (
                        <Skeleton height={360} />
                    ) : !customerInsights?.new_returning_conversion?.length ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No new vs returning conversion data available for the selected date range</p>
                        </Box>
                    ) : (
                        <NewReturningVisitorConversionChart
                            data={customerInsights.new_returning_conversion}
                            period={period}
                        />
                    )}
                </CardContent>
            </Card>

            <Grid container spacing={2} sx={{ marginTop: 1 }}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardHeader
                            title="Time to Register / Complete Profile"
                            subheader="Groups customers by how quickly they acted. Registered customers are grouped by time from first website visit to registration. Completed profiles are grouped by time from registration to replacing the temporary user ID with real profile details."
                        />
                        <CardContent>
                            {customerInsightsLoading ? (
                                <Skeleton height={340} />
                            ) : (
                                <TimeToRegisterProfileChart data={customerInsights?.time_to_register_profile || []} />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardHeader
                            title="Active vs Dormant Customers"
                            subheader="Groups registered customers by their most recent website activity. This helps sales decide who is warm, who needs follow-up, and who may need reactivation."
                        />
                        <CardContent>
                            {customerInsightsLoading ? (
                                <Skeleton height={340} />
                            ) : (
                                <ActiveDormantCustomersChart data={customerInsights?.active_dormant_customers || []} />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card sx={{ marginTop: 3 }}>
                <CardHeader
                    title="Logged-in vs Anonymous Visitor Trend"
                    subheader="Shows how much website traffic came from known people versus unknown visitors. Logged-in visitors are people who logged in or registered during that period. Anonymous visitors browsed without logging in."
                />
                <CardContent>
                    {visitorLoginSplitLoading ? (
                        <Skeleton height={360} />
                    ) : visitorLoginSplit.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No visitor login data available for the selected date range</p>
                        </Box>
                    ) : (
                        <VisitorLoginSplitChart data={visitorLoginSplit} period={period} />
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: 3 }}>
                <CardHeader
                    title="Visitor Trends"
                    subheader="Shows unique website visitors during the selected date range, split between first-time visitors and people returning to the website."
                />
                <CardContent>
                    {visitorLoading ? (
                        <Skeleton height={300} />
                    ) : visitorData.length === 0 ? (
                        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No visitor data available for the selected date range</p>
                        </Box>
                    ) : (
                        <VisitorTrendChart data={visitorData} period={period} />
                    )}
                </CardContent>
            </Card>

            <VisitorBreakdownCharts data={visitorBreakdowns} loading={visitorBreakdownLoading} />
        </Box>
    );
};

export default DashboardCustomers;
